import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import { useGetThreadsForUser } from '@flowwai-frontend/bps-officer-web/ai-chat-support/data-access'
import {
   ChatConfigUIStateEnum,
   ChatHistoryOpenedFromInfo,
   ThreadModel,
   useAIChatUIStore
} from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import { showCRMFailureToastWithGivenMessage } from '@flowwai-frontend/bps-officer-web/common/ui'
import { ThreadEntityType } from '@flowwai-frontend/bps-officer-web/graphql'
import {
   LoadingWrapper,
   useCustomT
} from '@flowwai-frontend/shared/common-components'
import { isMobile } from '@flowwai-frontend/shared/utils/web'
import { BottomSheet } from '@flowwai-frontend/design-system'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useAccountStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   ChatHistoryOptionType,
   MAX_HISTORY_THREADS_TO_SHOW
} from '@flowwai-frontend/shared-web/ai-chat-support/ui'
import ChatHistoryController from '../ChatHistoryController/ChatHistoryController'
import NewThreadController from '../NewThreadController/NewThreadController'
import ThreadDetailedViewController from '../ThreadDetailedViewController/ThreadDetailedViewController'

import * as Styles from './styles'

interface Props {
   renderActionButtons: (
      chatHistoryOptions: ChatHistoryOptionType[],
      chatHistoryOpenedFromInfo: ChatHistoryOpenedFromInfo,
      onClickNewThread?: () => void
   ) => React.ReactElement
   onClose: () => void
   isChatBoxExpanded: boolean
   shouldShowChatHistoryInSidepeek: boolean
   setShouldShowChatHistoryInSidepeek: (value: boolean) => void
}

const AIChatSupportController = (props: Props): React.ReactElement => {
   const {
      renderActionButtons,
      onClose,
      isChatBoxExpanded,
      shouldShowChatHistoryInSidepeek,
      setShouldShowChatHistoryInSidepeek
   } = props
   const [threads, setThreads] = useState<ThreadModel[]>([])
   const [totalCount, setTotalCount] = useState<number | null>(null)
   const [chatQuery, setChatQuery] = useState<string>('')
   const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
   const [searchText, setSearchText] = useState<string>('')
   const getThreadsAPI = useGetThreadsForUser()
   const accountStore = useAccountStore()

   const { theme } = useAppTheme()
   const chatBoxTheme = theme.aiChatSupport.chatBox

   const hasMoreThreads = totalCount ? totalCount > threads.length : false

   const t = useCustomT('common:errorMessages')
   const aiChatUIStore = useAIChatUIStore()

   useEffect(() => {
      getThreads()
   }, [])

   const clearChatQuery = (): void => setChatQuery('')

   const getThreads = async () => {
      const responseData = await getThreadsAPI.triggerAPI({
         reqObj: {
            entityId: accountStore.accountId ?? '',
            entityType: ThreadEntityType.Account,
            pagination: {
               limit: MAX_HISTORY_THREADS_TO_SHOW,
               offset: 0
            }
         },
         onFailure(args) {
            showCRMFailureToastWithGivenMessage(
               args.data?.message ?? t('somethingWentWrong')
            )
         }
      })

      if (!responseData || !responseData?.threads[0]?.threadId) {
         return aiChatUIStore.setActiveUIStateEnum(
            ChatConfigUIStateEnum.SearchState
         )
      }
      setThreads(responseData.threads)
      setActiveThreadId(responseData.threads[0].threadId.toString())
      setTotalCount(responseData.totalCount)
   }

   const fetchMoreThreads = async () => {
      const responseData = await getThreadsAPI.fetchMoreAPI({
         reqObj: {
            entityId: accountStore.accountId ?? '',
            entityType: ThreadEntityType.Account,
            pagination: {
               limit: MAX_HISTORY_THREADS_TO_SHOW,
               offset: threads.length
            }
         },
         onFailure(args) {
            showCRMFailureToastWithGivenMessage(
               args.data?.message ?? t('somethingWentWrong')
            )
         }
      })

      if (!responseData) return

      setThreads(pre => [...pre, ...responseData.threads])
   }

   const onClickNewThread = (): void => {
      clearChatQuery()
      setActiveThreadId(null)
   }

   const onClickChatHistory = (threadId: string) => {
      setActiveThreadId(threadId)
      aiChatUIStore.setActiveUIStateEnum(ChatConfigUIStateEnum.ChatState)
   }

   const handleOnSendChat = async (query: string) => {
      aiChatUIStore.setActiveUIStateEnum(ChatConfigUIStateEnum.ChatState)
      setChatQuery(query)
   }

   const onCreateThread = (threadModel: ThreadModel): void => {
      setThreads(pre => [threadModel, ...pre])
   }

   const getChatThreadOptions = (): ChatHistoryOptionType[] => {
      const hasEmptyThreads = threads.length === 0
      if (hasEmptyThreads) return []

      const options = threads.map(eachThread => ({
         id: eachThread.threadId.toString(),
         title: eachThread.name
      }))

      return options
   }

   const onClickHistoryInExpandedView = () => {
      setShouldShowChatHistoryInSidepeek(true)
   }

   const onClickBackInChatHistory = () => {
      switch (aiChatUIStore.chatHistoryOpenedFromInfo?.stateEnum) {
         case ChatConfigUIStateEnum.SearchState: {
            aiChatUIStore.setActiveUIStateEnum(
               ChatConfigUIStateEnum.SearchState
            )
            aiChatUIStore.setChatHistoryOpenedFromInfo(null)

            break
         }
         case ChatConfigUIStateEnum.ChatState: {
            aiChatUIStore.setActiveUIStateEnum(ChatConfigUIStateEnum.ChatState)
            setActiveThreadId(
               aiChatUIStore.chatHistoryOpenedFromInfo?.threadId ?? ''
            )
            aiChatUIStore.setChatHistoryOpenedFromInfo(null)
            break
         }
      }
   }

   const renderSuccessView = (): React.ReactElement => {
      switch (aiChatUIStore.activeUIStateEnum) {
         case ChatConfigUIStateEnum.ChatState: {
            const threadName = activeThreadId
               ? threads.find(
                    thread => thread.threadId.toString() === activeThreadId
                 )?.name
               : undefined
            return (
               <ThreadDetailedViewController
                  key={activeThreadId}
                  threadId={activeThreadId}
                  threadName={threadName}
                  initialQuery={chatQuery}
                  onCreateThread={onCreateThread}
                  onClickNewThreadButton={onClickNewThread}
                  renderActionButtons={() =>
                     renderActionButtons(
                        getChatThreadOptions(),
                        {
                           stateEnum: ChatConfigUIStateEnum.ChatState,
                           threadId: activeThreadId
                        },
                        onClickNewThread
                     )
                  }
                  isChatBoxExpanded={isChatBoxExpanded}
                  onClickHistoryInExpandedView={onClickHistoryInExpandedView}
                  shouldShowChatHistoryInSidepeek={
                     shouldShowChatHistoryInSidepeek
                  }
               />
            )
         }
         case ChatConfigUIStateEnum.SearchState:
            return (
               <NewThreadController
                  onSendChat={handleOnSendChat}
                  renderActionButtons={() =>
                     renderActionButtons(getChatThreadOptions(), {
                        stateEnum: ChatConfigUIStateEnum.SearchState
                     })
                  }
                  isChatBoxExpanded={isChatBoxExpanded}
                  shouldShowChatHistoryInSidepeek={
                     shouldShowChatHistoryInSidepeek
                  }
                  isThreadsAvailable={threads.length > 0}
                  onClickHistoryInExpandedView={onClickHistoryInExpandedView}
               />
            )
         case ChatConfigUIStateEnum.ChatHistory:
            return (
               <ChatHistoryController
                  onClose={onClose}
                  onClickNewThread={onClickNewThread}
                  threads={threads}
                  onClickChatHistory={onClickChatHistory}
                  hasMoreThreads={hasMoreThreads}
                  fetchMoreThreads={fetchMoreThreads}
                  shouldShowFetchMoreErrorView={
                     getThreadsAPI.apiError ? true : false
                  }
                  searchText={searchText}
                  setSearchText={setSearchText}
                  onClickBack={onClickBackInChatHistory}
                  setShouldShowChatHistoryInSidepeek={
                     setShouldShowChatHistoryInSidepeek
                  }
                  isChatBoxExpanded={isChatBoxExpanded}
               />
            )
      }
   }

   const shouldShowChatHistoryInBS =
      isMobile() &&
      aiChatUIStore.activeUIStateEnum === ChatConfigUIStateEnum.ChatHistory

   return shouldShowChatHistoryInBS ? (
      <BottomSheet
         isOpen={true}
         onRequestClose={onClickBackInChatHistory}
         containerClassName='!px-[0]'
         contentClassName='!max-h-[97%]'
      >
         {renderSuccessView()}
      </BottomSheet>
   ) : (
      <div className={cn(Styles.containerStyles)}>
         <LoadingWrapper
            onRetry={getThreads}
            apiStatus={getThreadsAPI.apiStatus}
            apiError={getThreadsAPI.apiError}
         >
            <div className='w-full h-full flex'>
               <div
                  className={cn(
                     ' transition-[width] duration-500 ease-in-out ',
                     {
                        'w-[360px] border-r-1 ': shouldShowChatHistoryInSidepeek,
                        'w-0': !shouldShowChatHistoryInSidepeek
                     },
                     chatBoxTheme.chatHistoryBorderColor
                  )}
               >
                  {shouldShowChatHistoryInSidepeek ? (
                     <ChatHistoryController
                        onClose={onClose}
                        onClickNewThread={onClickNewThread}
                        threads={threads}
                        onClickChatHistory={onClickChatHistory}
                        hasMoreThreads={hasMoreThreads}
                        fetchMoreThreads={fetchMoreThreads}
                        shouldShowFetchMoreErrorView={
                           getThreadsAPI.apiError ? true : false
                        }
                        searchText={searchText}
                        setSearchText={setSearchText}
                        onClickBack={onClickBackInChatHistory}
                        setShouldShowChatHistoryInSidepeek={
                           setShouldShowChatHistoryInSidepeek
                        }
                        isChatBoxExpanded={isChatBoxExpanded}
                     />
                  ) : null}
               </div>
               {renderSuccessView()}
            </div>
         </LoadingWrapper>
      </div>
   )
}

export default observer(AIChatSupportController)
